import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, apiFetch, apiFetchJson } from '../../lib/apiFetch.js';
import type {
  QcAnswerInput,
  QcAnswerResponse,
  QcArchiveResponse,
  QcChecklistDetail,
  QcChecklistsResponse,
  QcCreateChecklistInput,
  QcCreateChecklistResponse,
  QcDeleteResponse,
  QcJoinResponse,
  QcShareResponse,
  QcTemplatesResponse,
  QcWorkflowResponse,
} from './contractsMirror.js';

/**
 * Queries and mutations for quality control.
 *
 * SYNC MODEL: on save and on reopen, which is what was chosen over live push. Two people working
 * different sections never collide. Two people answering the SAME item means the later save wins —
 * the server upserts on (checklist, item), so there is one row and it carries whoever answered
 * last. Nothing is lost; the other person simply does not see it until they reload.
 *
 * Saves are per ITEM, not per form. Somebody with a stale page open can never wipe a section by
 * saving over it, which matters when a checklist runs to ~300 items and two people are walking a
 * house at once.
 */

export const qcKeys = {
  templates: ['qc', 'templates'] as const,
  list: (params: QcListParams) =>
    [
      'qc',
      'checklists',
      params.status ?? null,
      params.jobId ?? null,
      params.mine ?? false,
      params.showArchived ?? false,
    ] as const,
  detail: (id: number) => ['qc', 'checklist', id] as const,
};

export interface QcListParams {
  status?: string;
  jobId?: number;
  mine?: boolean;
  /** Archived rows are out by default; this is the opt-in the api spells `show_archived=1`. */
  showArchived?: boolean;
}

export function useQcTemplatesQuery(enabled = true) {
  return useQuery<QcTemplatesResponse>({
    queryKey: qcKeys.templates,
    queryFn: () => apiFetchJson<QcTemplatesResponse>('/api/v1/qc/templates'),
    enabled,
    // Templates change when somebody publishes a version, which is rare.
    staleTime: 5 * 60_000,
  });
}

export function useQcChecklistsQuery(params: QcListParams) {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.jobId) search.set('jobId', String(params.jobId));
  if (params.mine) search.set('mine', '1');
  if (params.showArchived) search.set('show_archived', '1');
  const qs = search.toString();

  return useQuery<QcChecklistsResponse>({
    queryKey: qcKeys.list(params),
    queryFn: () => apiFetchJson<QcChecklistsResponse>(`/api/v1/qc/checklists${qs ? `?${qs}` : ''}`),
  });
}

export function useQcChecklistQuery(id: number | null) {
  return useQuery<QcChecklistDetail>({
    queryKey: qcKeys.detail(id ?? 0),
    queryFn: () => apiFetchJson<QcChecklistDetail>(`/api/v1/qc/checklists/${id}`),
    enabled: typeof id === 'number' && id > 0,
  });
}

export function useCreateQcChecklistMutation() {
  const queryClient = useQueryClient();
  return useMutation<QcCreateChecklistResponse, Error, QcCreateChecklistInput>({
    mutationFn: (body) =>
      apiFetchJson<QcCreateChecklistResponse>('/api/v1/qc/checklists', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['qc', 'checklists'] });
    },
  });
}

/**
 * Save one answer.
 *
 * Deliberately does NOT invalidate the whole checklist on success. A refetch of ~300 items after
 * every tap would fight the person still working through the section, and the server already
 * returns the saved row — the caller patches that one item in place. The list IS invalidated, so
 * the progress and outstanding counts stay honest.
 */
export function useQcAnswerMutation(checklistId: number) {
  const queryClient = useQueryClient();
  return useMutation<QcAnswerResponse, Error, { itemId: number } & QcAnswerInput>({
    mutationFn: ({ itemId, ...body }) =>
      apiFetchJson<QcAnswerResponse>(`/api/v1/qc/checklists/${checklistId}/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['qc', 'checklists'] });

      const saved = response.item;
      if (!saved) return;
      queryClient.setQueryData<QcChecklistDetail>(qcKeys.detail(checklistId), (previous) => {
        if (!previous?.sections) return previous;
        return {
          ...previous,
          sections: previous.sections.map((section) => ({
            ...section,
            items: section.items.map((item) =>
              item.id === saved.id
                ? {
                    ...item,
                    state: saved.state,
                    valueText: saved.valueText,
                    approvedByText: saved.approvedByText,
                    note: saved.note,
                    answeredByName: saved.answeredByName,
                    answeredAt: saved.answeredAt,
                  }
                : item,
            ),
          })),
        };
      });
    },
  });
}

/**
 * Attach a photo to an item.
 *
 * Multipart, so it goes through `apiFetch` rather than `apiFetchJson` — the body is FormData and
 * the browser must set its own boundary. Setting Content-Type by hand here produces a request the
 * server cannot parse, which surfaces as a confusing 400 rather than an obvious mistake.
 *
 * Invalidates the checklist because a photo changes what the item is ALLOWED to do: a
 * photo-required item cannot pass until one exists, so the gate has to re-evaluate.
 */
export function useQcPhotoMutation(checklistId: number) {
  const queryClient = useQueryClient();
  return useMutation<{ ok?: boolean }, Error, { itemId: number; file: File }>({
    mutationFn: async ({ itemId, file }) => {
      const form = new FormData();
      form.append('photo', file);
      const response = await apiFetch(
        `/api/v1/qc/checklists/${checklistId}/items/${itemId}/photos`,
        { method: 'POST', body: form },
      );
      const body = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
      } | null;
      if (!response.ok || !body || body.ok === false) {
        throw new ApiError(response.status, body?.error ?? 'upload_failed', body ?? null);
      }
      return body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qcKeys.detail(checklistId) });
    },
  });
}

/**
 * The two-stage workflow: submit, waive, send back, sign off.
 *
 * One hook for all four because they share a shape — a POST that changes the checklist's status and
 * whose refusals are the interesting part. Every one of them re-checks on the server; these
 * mutations exist to make the actions reachable, never to decide whether they are allowed.
 */
export function useQcWorkflowMutation(checklistId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    QcWorkflowResponse,
    Error,
    | { action: 'submit' }
    | { action: 'request-changes' }
    | { action: 'approve'; signerName: string; customerName?: string }
    | { action: 'waive'; itemId: number; reason: string }
  >({
    mutationFn: (input) => {
      const path =
        input.action === 'waive'
          ? `/api/v1/qc/checklists/${checklistId}/items/${input.itemId}/waive`
          : `/api/v1/qc/checklists/${checklistId}/${input.action}`;
      const { action, ...body } = input as Record<string, unknown> & { action: string };
      void action;
      return apiFetchJson<QcWorkflowResponse>(path, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qcKeys.detail(checklistId) });
      void queryClient.invalidateQueries({ queryKey: ['qc', 'checklists'] });
    },
  });
}

/**
 * Retire a checklist, or bring it back.
 *
 * Sends the target state explicitly rather than a toggle. A toggle computed from the row the client
 * last rendered un-archives whatever somebody else archived a moment ago, and neither of them ever
 * sees it happen — the server rejects a missing `archived` for the same reason.
 *
 * Invalidates the DETAIL as well as the list: archiving revokes the QR server-side, so a detail page
 * left open would keep offering a share link that has stopped working.
 */
export function useQcArchiveMutation(checklistId: number) {
  const queryClient = useQueryClient();
  return useMutation<QcArchiveResponse, Error, { archived: boolean }>({
    mutationFn: (body) =>
      apiFetchJson<QcArchiveResponse>(`/api/v1/qc/checklists/${checklistId}/archive`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['qc', 'checklists'] });
      void queryClient.invalidateQueries({ queryKey: qcKeys.detail(checklistId) });
    },
  });
}

/**
 * Destroy a checklist outright.
 *
 * The server refuses this with 409 `checklist_approved` once a checklist is signed off, and that
 * refusal is the real guard — hiding the control is only about not offering a button that cannot
 * work. On success the detail entry is REMOVED rather than invalidated, because refetching an id
 * that no longer exists just produces a 404 the screen has to explain.
 */
export function useQcDeleteMutation(checklistId: number) {
  const queryClient = useQueryClient();
  return useMutation<QcDeleteResponse, Error, void>({
    mutationFn: () =>
      apiFetchJson<QcDeleteResponse>(`/api/v1/qc/checklists/${checklistId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: qcKeys.detail(checklistId) });
      void queryClient.invalidateQueries({ queryKey: ['qc', 'checklists'] });
    },
  });
}

/** Mint the link a QR encodes. Re-minting replaces the previous one, which the UI states. */
export function useQcShareMutation(checklistId: number) {
  return useMutation<QcShareResponse, Error, void>({
    mutationFn: () =>
      apiFetchJson<QcShareResponse>(`/api/v1/qc/checklists/${checklistId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }),
  });
}

/**
 * Redeem a scanned link.
 *
 * POST with the token in the BODY, never a GET with it in the path — a token in a URL ends up in
 * browser history, server logs and any Referer the page later sends.
 */
export function useQcJoinMutation() {
  const queryClient = useQueryClient();
  return useMutation<QcJoinResponse, Error, { token: string }>({
    mutationFn: (body) =>
      apiFetchJson<QcJoinResponse>('/api/v1/qc/join', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['qc'] });
    },
  });
}

export function useAssignQcMutation(checklistId: number) {
  const queryClient = useQueryClient();
  return useMutation<{ ok?: boolean }, Error, { userId: number }>({
    mutationFn: (body) =>
      apiFetchJson<{ ok?: boolean }>(`/api/v1/qc/checklists/${checklistId}/assignees`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qcKeys.detail(checklistId) });
    },
  });
}
